import { useState, useEffect } from "react";
import { API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from "recharts";
import {
  Users,
  TrendingUp,
  TrendingDown,
  Activity,
  Clock,
  CheckCircle,
  XCircle,
  Target,
  Award,
  Calendar,
  Loader2,
  ChevronRight,
  AlertTriangle
} from "lucide-react";

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

export default function AdminReports({ token, users, groups }) {
  const [view, setView] = useState("overview"); // overview, user, group
  const [period, setPeriod] = useState("week");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [leaderboard, setLeaderboard] = useState(null);
  const [trends, setTrends] = useState(null);
  const [userReport, setUserReport] = useState(null);
  const [groupReport, setGroupReport] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (view === "overview") {
      fetchOverviewData();
    }
  }, [view, period]);

  useEffect(() => {
    if (view === "user" && selectedUserId) {
      fetchUserReport();
    }
  }, [selectedUserId, period]);

  useEffect(() => {
    if (view === "group" && selectedGroupId) {
      fetchGroupReport();
    }
  }, [selectedGroupId, period]);

  const fetchOverviewData = async () => {
    setLoading(true);
    try {
      const [leaderboardRes, trendsRes] = await Promise.all([
        axios.get(`${API}/v2/reports/leaderboard?period=${period}`, { headers }),
        axios.get(`${API}/v2/reports/activity-trends?days=14`, { headers })
      ]);
      setLeaderboard(leaderboardRes.data);
      setTrends(trendsRes.data);
    } catch (error) {
      console.error("Error fetching overview:", error);
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/v2/reports/user/${selectedUserId}?period=${period}`,
        { headers }
      );
      setUserReport(response.data);
    } catch (error) {
      toast.error("Failed to load user report");
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/v2/reports/group/${selectedGroupId}?period=${period}`,
        { headers }
      );
      setGroupReport(response.data);
    } catch (error) {
      toast.error("Failed to load group report");
    } finally {
      setLoading(false);
    }
  };

  const workers = users?.filter(u => u.role === "worker") || [];

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Activity Trends Chart */}
      {trends && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              Activity Trends (Last 14 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trends.trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="day" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="tasks_started" name="Tasks Started" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                  <Area type="monotone" dataKey="tasks_completed" name="Tasks Completed" stroke="#10b981" fill="#10b981" fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leaderboard Section */}
      {leaderboard && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Most Active */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                Most Active Workers
                <Badge variant="outline" className="ml-auto">{period}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(leaderboard.most_active || []).slice(0, 5).map((worker, idx) => (
                  <div
                    key={worker.user_id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedUserId(worker.user_id);
                      setView("user");
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                        idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-amber-700' : 'bg-slate-300'
                      }`}>
                        {idx + 1}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{worker.name}</p>
                        <p className="text-xs text-slate-500">{worker.country || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-slate-900">{worker.tasks_completed}</p>
                      <p className="text-xs text-slate-500">{worker.completion_rate}% rate</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Least Active / Inactive */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-500" />
                Least Active Workers
                {leaderboard.inactive_count > 0 && (
                  <Badge variant="destructive" className="ml-2">
                    {leaderboard.inactive_count} inactive
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(leaderboard.least_active || []).slice(0, 5).map((worker, idx) => (
                  <div
                    key={worker.user_id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedUserId(worker.user_id);
                      setView("user");
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                        {worker.tasks_started === 0 ? (
                          <AlertTriangle className="w-4 h-4 text-red-500" />
                        ) : (
                          <span className="text-red-600 font-medium">{idx + 1}</span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{worker.name}</p>
                        <p className="text-xs text-slate-500">
                          {worker.last_activity 
                            ? `Last active: ${new Date(worker.last_activity).toLocaleDateString()}`
                            : 'Never active'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-slate-900">{worker.tasks_started}</p>
                      <p className="text-xs text-slate-500">tasks started</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Summary Stats */}
      {leaderboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{leaderboard.total_workers}</p>
                  <p className="text-xs text-slate-500">Active Workers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {(leaderboard.most_active || []).reduce((sum, w) => sum + w.tasks_completed, 0)}
                  </p>
                  <p className="text-xs text-slate-500">Tasks Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {Math.round((leaderboard.most_active || []).reduce((sum, w) => sum + w.completion_rate, 0) / Math.max((leaderboard.most_active || []).length, 1))}%
                  </p>
                  <p className="text-xs text-slate-500">Avg Completion Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{leaderboard.inactive_count}</p>
                  <p className="text-xs text-slate-500">Inactive Workers</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );

  const renderUserReport = () => {
    if (!userReport) return null;
    
    const { user, metrics, submissions, daily_activity } = userReport;
    
    const pieData = [
      { name: 'Approved', value: submissions.approved, color: '#10b981' },
      { name: 'Rejected', value: submissions.rejected, color: '#ef4444' },
      { name: 'Pending', value: submissions.pending, color: '#f59e0b' }
    ].filter(d => d.value > 0);

    return (
      <div className="space-y-6">
        {/* User Header */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">
                    {user.name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{user.name}</h2>
                  <p className="text-slate-500">{user.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>
                      {user.status}
                    </Badge>
                    <span className="text-sm text-slate-500">{user.country}</span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-amber-600">{user.points_balance || 0}</p>
                <p className="text-sm text-slate-500">Total Points</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-600">{metrics.tasks_started}</p>
              <p className="text-xs text-slate-500">Tasks Started</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{metrics.tasks_completed}</p>
              <p className="text-xs text-slate-500">Completed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-600">{metrics.tasks_expired}</p>
              <p className="text-xs text-slate-500">Expired</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-600">{metrics.completion_rate}%</p>
              <p className="text-xs text-slate-500">Completion Rate</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-cyan-600">{metrics.avg_completion_time_minutes}</p>
              <p className="text-xs text-slate-500">Avg Time (min)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-600">{metrics.points_earned}</p>
              <p className="text-xs text-slate-500">Points Earned</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily Activity */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Daily Activity (Last 7 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={daily_activity}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="day" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="started" name="Started" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="completed" name="Completed" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Submission Status Pie */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Submission Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400">
                    No submissions yet
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const renderGroupReport = () => {
    if (!groupReport) return null;
    
    const { group, metrics, member_performance } = groupReport;

    return (
      <div className="space-y-6">
        {/* Group Header */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">{group.name}</h2>
                <p className="text-slate-500">{group.description || 'No description'}</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{metrics.total_members}</p>
                  <p className="text-xs text-slate-500">Members</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{metrics.active_members}</p>
                  <p className="text-xs text-slate-500">Active</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Group Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-600">{metrics.tasks_started}</p>
              <p className="text-xs text-slate-500">Tasks Started</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{metrics.tasks_completed}</p>
              <p className="text-xs text-slate-500">Completed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-600">{metrics.completion_rate}%</p>
              <p className="text-xs text-slate-500">Completion Rate</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-600">{metrics.total_points}</p>
              <p className="text-xs text-slate-500">Total Points</p>
            </CardContent>
          </Card>
        </div>

        {/* Member Performance Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Member Performance Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={member_performance.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" stroke="#64748b" fontSize={12} />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    stroke="#64748b" 
                    fontSize={11} 
                    width={100}
                    tickFormatter={(value) => value.length > 12 ? value.slice(0, 12) + '...' : value}
                  />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="tasks_completed" name="Completed" fill="#10b981" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="tasks_started" name="Started" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Member Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Member Details</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rank</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Started</TableHead>
                  <TableHead className="text-right">Completed</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Points</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {member_performance.map((member, idx) => (
                  <TableRow
                    key={member.user_id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => {
                      setSelectedUserId(member.user_id);
                      setView("user");
                    }}
                  >
                    <TableCell>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                        idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-amber-700' : 'bg-slate-300'
                      }`}>
                        {idx + 1}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{member.name}</TableCell>
                    <TableCell>
                      <Badge variant={member.status === 'active' ? 'default' : 'secondary'}>
                        {member.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{member.tasks_started}</TableCell>
                    <TableCell className="text-right">{member.tasks_completed}</TableCell>
                    <TableCell className="text-right">{member.completion_rate}%</TableCell>
                    <TableCell className="text-right font-medium text-amber-600">
                      {member.points_earned}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Navigation Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button
            variant={view === "overview" ? "default" : "outline"}
            size="sm"
            onClick={() => setView("overview")}
            data-testid="reports-overview-btn"
          >
            <Activity className="w-4 h-4 mr-1" />
            Overview
          </Button>
          <Button
            variant={view === "user" ? "default" : "outline"}
            size="sm"
            onClick={() => setView("user")}
            data-testid="reports-user-btn"
          >
            <Users className="w-4 h-4 mr-1" />
            User Report
          </Button>
          <Button
            variant={view === "group" ? "default" : "outline"}
            size="sm"
            onClick={() => setView("group")}
            data-testid="reports-group-btn"
          >
            <Award className="w-4 h-4 mr-1" />
            Group Report
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {view === "user" && (
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger className="w-[200px]" data-testid="select-user-report">
                <SelectValue placeholder="Select worker" />
              </SelectTrigger>
              <SelectContent>
                {workers.map(w => (
                  <SelectItem key={w.user_id} value={w.user_id}>
                    {w.name || w.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {view === "group" && (
            <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
              <SelectTrigger className="w-[200px]" data-testid="select-group-report">
                <SelectValue placeholder="Select group" />
              </SelectTrigger>
              <SelectContent>
                {groups?.map(g => (
                  <SelectItem key={g.group_id} value={g.group_id}>
                    {g.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[130px]" data-testid="select-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}

      {/* Content */}
      {!loading && (
        <>
          {view === "overview" && renderOverview()}
          {view === "user" && (
            selectedUserId ? renderUserReport() : (
              <Card>
                <CardContent className="p-12 text-center text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Select a worker to view their performance report</p>
                </CardContent>
              </Card>
            )
          )}
          {view === "group" && (
            selectedGroupId ? renderGroupReport() : (
              <Card>
                <CardContent className="p-12 text-center text-slate-500">
                  <Award className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Select a group to view their performance report</p>
                </CardContent>
              </Card>
            )
          )}
        </>
      )}
    </div>
  );
}
